import React, { useState } from "react";
import {
	Box,
	Button,
	Container,
	Grid,
	TextField,
	Typography,
	MenuItem,
	IconButton,
	Breadcrumbs,
	Link,
	Divider,
	Tooltip,
	Avatar,
	Autocomplete,
	FormControlLabel,
	Checkbox,
	useTheme,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form, ErrorMessage, getIn } from "formik";
import * as Yup from "yup";
import CompulsoryFieldsLabel from "../../helpers/CompulsoryFields";
import queryGraphql from "../../helpers/ApiInterface";
import { useAuth } from "../../helpers/AuthProvider";
import MiniBot from "../MiniBot";
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../MainDrawer"; // Adjust path if needed
const clusterTypeList = ["Autopilot", "Standard"];
const validationSchema = Yup.object().shape({
	clusterName: Yup.string()
		.required("This field is required")
		.strict()
		.lowercase("This field must be in lowercase")
		.matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
		.max(20, "This field must not exceed 20 characters"),
	gcpProjectId: Yup.string()
		.required("This field is required")
		.strict()
		.lowercase("This field must be in lowercase")
		.matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
		.max(30, "This field must not exceed 30 characters"),
	region: Yup.string()
		.required("This field is required")
		.matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
		.strict(),
	gcpClusterViewers: Yup.array()
		.of(
			Yup.string()
				.required("This field is required")
				.matches(/^[a-zA-Z0-9._%+-]+@searce\.com$/, "Invalid domain")
				.strict()
		)
		.required("This field is required")
		.min(1, "This field is required")
		.strict(),
	applicationRequesters: Yup.array()
		.of(
			Yup.string()
				.required("This field is required")
				.matches(/^[a-zA-Z0-9._%+-]+@searce\.com$/, "Invalid domain")
				.strict()
		)
		.required("This field is required")
		.min(1, "This field is required")
		.strict(),
	costLabelPairs: Yup.array()
		.of(
			Yup.object({
				cost: Yup.string()
					.required("This field is required")
					.strict()
					.lowercase("This field must be in lowercase")
					.matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
					.max(20, "This field must not exceed 20 characters"),
				label: Yup.string()
					.required("This field is required")
					.strict()
					.lowercase("This field must be in lowercase")
					.matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
					.max(20, "This field must not exceed 20 characters"),
			})
		)
		.required("This field is required"),
	environment: Yup.string().required("This field is required").oneOf(["dev", "staging", "prod"]),
	//clusterSize: Yup.string().required("This field is required").oneOf(["small", "medium", "large"]),
	clusterType: Yup.string().required("This field is required").oneOf(clusterTypeList),
	departmentValue: Yup.string()
		.required("This field is required")
		.strict()
		.lowercase("This field must be in lowercase")
		.matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
		.max(30, "This field must not exceed 30 characters"),
	costCenterValue: Yup.string()
		.required("This field is required")
		.strict()
		.lowercase("This field must be in lowercase")
		.matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
		.max(20, "This field must not exceed 20 characters"),
	
	agreement1: Yup.boolean()
        .oneOf([true], "You must agree to this condition to proceed."),
    agreement2: Yup.boolean()
        .oneOf([true], "You must agree to this condition to proceed."),
});

const regionList = [
	"africa-south1",
	"asia-east1",
	"asia-east2",
	"asia-northeast1",
	"asia-northeast2",
	"asia-northeast3",
	"asia-south1",
	"asia-south2",
	"asia-southeast1",
	"asia-southeast2",
	"australia-southeast1",
	"australia-southeast2",
	"europe-central2",
	"europe-north1",
	"europe-southwest1",
	"europe-west1",
	"europe-west10",
	"europe-west12",
	"europe-west2",
	"europe-west3",
	"europe-west4",
	"europe-west6",
	"europe-west8",
	"europe-west9",
	"me-central1",
	"me-central2",
	"me-west1",
	"northamerica-northeast1",
	"northamerica-northeast2",
	"southamerica-east1",
	"southamerica-west1",
	"us-central1",
	"us-east1",
	"us-east4",
	"us-east5",
	"us-south1",
	"us-west1",
	"us-west2",
	"us-west3",
	"us-west4",
];
interface ClusterFormProps {
	isEdit: boolean;
}
interface ClusterFormContentProps {
	isEdit: boolean;
}
interface oneCluster {
	id: string;
	clusterName: string;
	environment: string;
	status: string;
  }
const ExistingClusterFormContent: React.FC<ClusterFormContentProps> = ({ isEdit }) => {
	const navigate = useNavigate();
	const [users, setUsers] = useState([]);
	const { user } = useAuth();
	const isViewer = user.roles?.some((role: string) => role === "viewer") || false;
	const [cluster, setCluster] = useState<any>({});
	const [clusters, setClusters] = useState<any>({});
	const { id } = useParams();
	const [devDisable, setDevDisable] = useState<boolean>(false);
	const [stageDisable, setStageDisable] = useState<boolean>(false);
	const [prodDisable, setProdDisable] = useState<boolean>(false);
	const [ClusterName, setClusterName] = useState<string>();
	const [dummy, setDummy] = useState<any>();
	const [config, setConfig] = useState<any>();

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const data = await queryGraphql(
					`
                        query($clusterId: String!){
                        cluster(id:$clusterId) {
						id,
                        clusterName,
                        clusterOwner{
                        id,
                        name,
                        googleProfilePicture
                        },
                        gcpProjectId,
                        environment,
                        region,
                        clusterSize,
                        clusterType,
                        gcpClusterViewers,
                        applicationRequesters,
                        costLabelPairs{
                        cost,
                        label
                        },
                        status,
                        createdAt,
						
                        }
                        }
`,
					{ clusterId: id }
				);
				if (data.cluster) {
					setCluster(data.cluster);
				}
			} catch (error) {
				console.log(error);
			}
		};

		fetchData();
	}, []);

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const data = await queryGraphql(`
                        query Clusters {
							clusters {
								id
								clusterName
								environment
								status
							}
							}
							`);
				if (data.clusters) {
					setClusters(data.clusters.filter((e:any) => e.status !== "rejected"));
					// console.log("here",clusters);
				}
				setDevDisable(false);
				setStageDisable(true);
				setProdDisable(true);
				clusters.forEach((item: oneCluster) => {
					
					(ClusterName == item.clusterName && "dev" == item.environment )? setDevDisable(true): (ClusterName == item.clusterName && "staging" == item.environment )? setStageDisable(true) : (ClusterName == item.clusterName && "prod" == item.environment? setProdDisable(true) : setDummy(true) ) ;
				
					if(ClusterName == item.clusterName && "dev" == item.environment ){
						setDevDisable(true);
						setStageDisable(false);
						setProdDisable(false);
					}
					if(ClusterName == item.clusterName && "staging" == item.environment ){
						setDevDisable(true);
						setStageDisable(true);
						setProdDisable(false);
					}
					if(ClusterName == item.clusterName && "prod" == item.environment){
						setDevDisable(true);
						setStageDisable(true);
						setProdDisable(true);
					}
				
					// console.log("Dev cluster name->",ClusterName);
				  });
				//   console.log('dev->',devDisable)
				//   console.log('stage->',stageDisable)
				//   console.log('prod->',prodDisable)
				
				  
			} catch (error) {
				console.log(error);
			}
		};

		fetchData();
	}, [ClusterName]);

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const data = await queryGraphql(`
                        query{
                        users {
						id,
                        email
                        }
                        }
`);
				if (data.users) {
					setUsers(data.users);
				}
			} catch (error) {
				console.log(error);
			}
		};

		fetchData();
	}, []);


	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const data = await queryGraphql(`
                     query Query {
						getConfig {
							serviceAccountRunner
						}
						}
`);
				if (data.getConfig ) {
					setConfig(data.getConfig );
				}
			} catch (error) {
				console.log(error);
			}
		};

		fetchData();
	}, []);


	return (
		<Container
			disableGutters
			maxWidth={false}
			sx={{ padding: "24px", bgcolor: "#fafafa", minHeight: "100vh" }}
		>
			<MiniBot/>
			<Box sx={{ mb: 4 }}>
				<Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
					<Link underline="hover" color="inherit" href="/clusters">
						Clusters
					</Link>
					<Typography color="text.primary">
						{isEdit ? "Edit Cluster Details" : "Add Existing Cluster"}
					</Typography>
				</Breadcrumbs>
				<Box display="flex" alignItems="center" mt={2}>
					<IconButton
						edge="start"
						aria-label="back"
						color="inherit"
						onClick={() => navigate("/clusters")}
					>
						<ArrowBackIosNewIcon fontSize="small" />
					</IconButton>
					<Typography fontFamily="Gilroy-Bold" fontSize="18px">
						{isEdit ? "Edit Cluster Details" : "Add Existing Cluster"}
					</Typography>
				</Box>
			</Box>
			<Grid container>
				<Grid item xs={1}></Grid>
				<Grid item xs={10}>
					{((isEdit && cluster.id) || !isEdit) && (
						<Formik
							initialValues={{
								clusterName: isEdit ? cluster.clusterName : "",
								gcpProjectId: isEdit ? cluster.gcpProjectId : "",
								region: isEdit ? cluster.region : "",
								gcpClusterViewers: isEdit ? cluster.gcpClusterViewers : [],
								applicationRequesters: isEdit ? cluster.applicationRequesters : [],
								costLabelPairs: isEdit
									? cluster.costLabelPairs?.filter(
											(e: any) => !["department", "cost-center", "cluster-owner"].includes(e.cost)
									  )
									: [],
								environment: isEdit ? cluster.environment : "",
								//clusterSize: isEdit ? cluster.clusterSize : "",
								clusterType: isEdit ? cluster.clusterType : "",
								departmentValue: isEdit
									? cluster.costLabelPairs?.find((elem: any) => elem.cost == "department").label
									: "",
								costCenterValue: isEdit
									? cluster.costLabelPairs?.find((elem: any) => elem.cost == "cost-center").label
									: "",
								agreement1: false,
								agreement2: false,
							}}
							validationSchema={validationSchema}
							onSubmit={async ({
								applicationRequesters,
								clusterType,
								costLabelPairs,
								environment,
								gcpProjectId,
								clusterName,
								//clusterSize,
								gcpClusterViewers,
								region,
								departmentValue,
								costCenterValue,
							}) => {
								if (!isEdit) {
									let keyValuePairs = [];
									keyValuePairs.push(
										{
											cost: "cluster-owner",
											label: (user.name as string).toLowerCase().replace(" ", "-"),
										},
										{
											cost: "department",
											label: departmentValue,
										},
										{
											cost: "cost-center",
											label: costCenterValue,
										}
									);
									keyValuePairs.push(
										...costLabelPairs.map((e: any) => {
											return { cost: e.cost, label: e.label };
										})
									);
									const data = await queryGraphql(
										`
					                            mutation Mutation($input: ExistingClusterInput!) {
													addExistingCluster(input: $input) {
														id
													}
													}
					    `,
										{
											input: {
												applicationRequesters,
												clusterType,
												costLabelPairs: keyValuePairs,
												environment,
												gcpProjectId,
												clusterName,
												clusterSize:"default",
												gcpClusterViewers,
												region,
												// clusterOwner: user?.email || null,
											},
										}
									);

									const info = await queryGraphql(`mutation Mutation($input: ResourceStatusCreateInput) {
																	createResourceStatus(input: $input) {
																		id
																	}
																	}`, {
																		input: {
																		  currentStatus: "pending",
																		  resourceEnvironment: environment,
																		  resourceId: data.addExistingCluster.id,
																		  resourceName: clusterName,
																		  resourceType: "cluster"
																		}
																	  });
																	  console.log("Hello",data.addExistingCluster.id);
																	  console.log("hello2",info.createResourceStatus.id);								  
									if (data.addExistingCluster.id && info.createResourceStatus.id) {
										
										navigate("/clusters");
									}
									return;
								} else {
									let keyValuePairs = [];
									keyValuePairs.push(
										{
											cost: "cluster-owner",
											label: (user.name as string).toLowerCase().replace(" ", "-"),
										},
										{
											cost: "department",
											label: departmentValue,
										},
										{
											cost: "cost-center",
											label: costCenterValue,
										}
									);
									keyValuePairs.push(
										...costLabelPairs.map((e: any) => {
											return { cost: e.cost, label: e.label };
										})
									);
									const data = await queryGraphql(
										`
					                           mutation UpdateCluster($input: ClusterUpdateInput) {
								updateCluster(input: $input) {
								  id
								}
							  }
					    `,
										{
											input: {
												id: id,
												applicationRequesters,
												clusterType,
												costLabelPairs: keyValuePairs,
												environment,
												gcpProjectId,
												clusterName,
												//clusterSize,
												gcpClusterViewers,
												region,
											},
										}
									);
									const info = await queryGraphql(`mutation Mutation($input: ResourceStatusUpdateInput) {
																	updateResourceStatus(input: $input) {
																		id
																	}
																	}`, {
																		input: {
																		  currentStatus: "pending",
																		  resourceId: id,
																		  resourceName: clusterName
																		}
																	  });

									if (data.updateCluster.id && info.updateResourceStatus.id) {
										navigate("/clusters");
									}
									return;
								}
							}}
						>
							{({
								handleChange,
								handleBlur,
								values,
								setFieldValue,
								errors,
								touched,
								isValid,
								dirty,
							}) => {
								return (							
									<Form>
										<Box>
											<Typography fontFamily="Gilroy-Medium" fontSize="16px" mb={4}>
												{isEdit
													? "Make changes to below details and save"
													: "Fill the below details to add an existing cluster"}
											</Typography>

											<div
												style={{
													backgroundColor: "white",
													padding: "24px",
													borderRadius: "8px",
													boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
													marginBottom: "24px",
												}}
											>
												<Typography
													fontFamily="Gilroy-Medium"
													fontSize="14px"
													style={{ marginBottom: "16px" }}
												>
													Basic Information
												</Typography>
												<Divider sx={{ marginBottom: "24px" }} />
												<Grid container spacing={4} style={{ marginBottom: "16px" }}>
													<Grid item xs={12} sm={6}>
														<CompulsoryFieldsLabel fieldName="Cluster Name" />
														<TextField
															sx={{
																"& .MuiFilledInput-root": {
																	border:
																		errors["clusterName"] && touched["clusterName"]
																			? "1px solid red"
																			: "1px solid #00000000",

																	"&.Mui-focused": {
																		backgroundColor: "white",
																		border: "1px solid black",
																	},
																},
															}}
															variant="filled"
															fullWidth
															name="clusterName"
															value={values.clusterName}
															// onChange={handleChange}
															onChange= { (e) =>{
															handleChange(e);
															// console.log("e --> ",e)
															setClusterName(e.target.value);
															// console.log("useState value",values.clusterName);
															}}
															onBlur={handleBlur}
															placeholder="Enter name here"
															size="medium"
															hiddenLabel
															InputProps={{
																disableUnderline: true,

																style: {
																	borderRadius: "8px",
																},
															}}
														/>
														<div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
															<ErrorMessage name="clusterName" component="div" />
														</div>
													</Grid>
													<Grid item xs={12} sm={6}>
														<CompulsoryFieldsLabel fieldName="GCP Project ID" />
														<TextField
															variant="filled"
															fullWidth
															name="gcpProjectId"
															value={values.gcpProjectId}
															onChange={handleChange}
															onBlur={handleBlur}
															placeholder="Select"
															size="medium"
															sx={{
																"& .MuiFilledInput-root": {
																	border:
																		errors["gcpProjectId"] && touched["gcpProjectId"]
																			? "1px solid red"
																			: "1px solid #00000000",
																	"&.Mui-focused": {
																		backgroundColor: "white",
																		border: "1px solid black",
																	},
																},
															}}
															hiddenLabel
															InputProps={{
																disableUnderline: true,
																style: {
																	borderRadius: "8px",
																},
															}}
														/>
														<div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
															<ErrorMessage name="gcpProjectId" component="div" />
														</div>
													</Grid>
													<Grid item xs={12} sm={6}>
														<CompulsoryFieldsLabel fieldName="Cluster Type" />
														<TextField
															select
															placeholder="Select"
															name="clusterType"
															value={values.clusterType}
															onChange={handleChange}
															onBlur={handleBlur}
															variant="filled"
															fullWidth
															hiddenLabel
															size="medium"
															sx={{
																"& .MuiFilledInput-root": {
																	border:
																		errors["clusterType"] && touched["clusterType"]
																			? "1px solid red"
																			: "1px solid #00000000",
																	"&.Mui-focused": {
																		backgroundColor: "white",
																		border: "1px solid black",
																	},
																},
															}}
															InputProps={{
																disableUnderline: true,
																style: {
																	borderRadius: "8px",
																},
															}}
														>
															{clusterTypeList.map((e) => (
																<MenuItem
																	value={e}
																	onClick={() => setFieldValue("clusterType", e)}
																>
																	{e}
																</MenuItem>
															))}
														</TextField>
														<div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
															<ErrorMessage name="clusterType" component="div" />
														</div>
													</Grid>
													<Grid item xs={12} sm={6}>
														<CompulsoryFieldsLabel fieldName="Region" />
														<Autocomplete
															options={regionList}
															filterSelectedOptions
															defaultValue={values.region}
															onChange={(e, newVal, reason) => {
																// setApplicationOwners(newVal);

																setFieldValue("region", newVal);
															}}
															renderInput={(params) => (
																<TextField
																	{...params}
																	variant="filled"
																	fullWidth
																	placeholder="Select"
																	size="medium"
																	hiddenLabel
																	sx={{
																		"& .MuiFilledInput-root": {
																			border:
																				errors["region"] && touched["region"]
																					? "1px solid red"
																					: "1px solid #00000000",
																			"&.Mui-focused": {
																				backgroundColor: "white",
																				border: "1px solid black",
																			},
																		},
																	}}
																	InputProps={{
																		...params.InputProps,
																		style: {
																			borderRadius: "8px",
																		},

																		disableUnderline: true,
																	}}
																/>
															)}
														/>

														<div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
															{getIn(errors, `region`)}
														</div>
													</Grid>
													<Grid item xs={12} sm={6}>
														<CompulsoryFieldsLabel fieldName="Environment" />
														<Box display="flex" gap={2} mt="14px">
															<Button
																sx={{
																	borderRadius: 5,
																	borderColor: values.environment == "dev" ? "#000000" : "#666666",
																	color: values.environment == "dev" ? "#000000" : "#666666",
																	bgcolor: values.environment == "dev" ? "#E0E0E0" : "#FFFFFF",
																	textTransform: "none",
																}}
																variant="outlined"
																// onClick={() => setEnvironment("dev")}
																onClick={() => setFieldValue("environment", "dev")}
																disabled= {devDisable}
															>
																dev
															</Button>
															<Button
																sx={{
																	borderRadius: 5,
																	borderColor:
																		values.environment == "staging" ? "#000000" : "#666666",
																	color: values.environment == "staging" ? "#000000" : "#666666",
																	bgcolor: values.environment == "staging" ? "#E0E0E0" : "#FFFFFF",
																	textTransform: "none",
																}}
																variant="outlined"
																// onClick={() => setEnvironment("STAGING")}
																onClick={() => setFieldValue("environment", "staging")}
																disabled={stageDisable}
															>
																staging
															</Button>
															<Button
																sx={{
																	borderRadius: 5,
																	borderColor: values.environment == "prod" ? "#000000" : "#666666",
																	color: values.environment == "prod" ? "#000000" : "#666666",
																	bgcolor: values.environment == "prod" ? "#E0E0E0" : "#FFFFFF",
																	textTransform: "none",
																}}
																variant="outlined"
																// onClick={() => setEnvironment("PRODUCTION")}
																onClick={() => setFieldValue("environment", "prod")}
																disabled={prodDisable}
															>
																prod
															</Button>
														</Box>
														<div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
															<ErrorMessage name="environment" component="div" />
														</div>
													</Grid>
													{/* <Grid item xs={12} sm={6}>
														<CompulsoryFieldsLabel fieldName="Cluster Size" />

														<Box display="flex" gap={2} mt="14px">
															<Button
																sx={{
																	borderRadius: 5,
																	borderColor:
																		values.clusterSize == "small" ? "#000000" : "#666666",
																	color: values.clusterSize == "small" ? "#000000" : "#666666",
																	bgcolor: values.clusterSize == "small" ? "#E0E0E0" : "#FFFFFF",
																	textTransform: "none",
																}}
																variant="outlined"
																// onClick={() => setClusterSize("small")}
																onClick={() => setFieldValue("clusterSize", "small")}
															>
																small
															</Button>
															<Button
																sx={{
																	borderRadius: 5,
																	borderColor:
																		values.clusterSize == "medium" ? "#000000" : "#666666",
																	color: values.clusterSize == "medium" ? "#000000" : "#666666",
																	bgcolor: values.clusterSize == "medium" ? "#E0E0E0" : "#FFFFFF",
																	textTransform: "none",
																}}
																variant="outlined"
																// onClick={() => setClusterSize("medium")}
																onClick={() => setFieldValue("clusterSize", "medium")}
															>
																medium
															</Button>
															<Button
																sx={{
																	borderRadius: 5,
																	borderColor:
																		values.clusterSize == "large" ? "#000000" : "#666666",
																	color: values.clusterSize == "large" ? "#000000" : "#666666",
																	bgcolor: values.clusterSize == "large" ? "#E0E0E0" : "#FFFFFF",
																	textTransform: "none",
																}}
																variant="outlined"
																// onClick={() => setClusterSize("large")}
																onClick={() => setFieldValue("clusterSize", "large")}
															>
																large
															</Button>
														</Box>
														<div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
															<ErrorMessage name="clusterSize" component="div" />
														</div>
													</Grid> */}
												</Grid>
											</div>

											<div
												style={{
													backgroundColor: "white",
													padding: "24px",
													borderRadius: "8px",
													boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
													marginBottom: "24px",
												}}
											>
												<Typography
													fontFamily="Gilroy-Medium"
													fontSize="14px"
													style={{ marginBottom: "16px" }}
												>
													Cluster Viewers and Application Owners
												</Typography>
												<Divider sx={{ marginBottom: "24px" }} />
												<Grid container spacing={4} style={{ marginBottom: "16px" }}>
													<Grid item xs={12} sm={6}>
														<CompulsoryFieldsLabel fieldName="Cluster Viewers" />
														<Autocomplete
															multiple
															options={users.map((e: any) => e.email)}
															filterSelectedOptions
															ChipProps={{ size: "small" }}
															freeSolo
															defaultValue={values.gcpClusterViewers}
															onChange={(e, newVal, reason) => {
																// setApplicationOwners(newVal);
																setFieldValue("gcpClusterViewers", newVal);
															}}
															renderInput={(params) => (
																<TextField
																	{...params}
																	variant="filled"
																	fullWidth
																	placeholder="e.g. abc121@gmail.com"
																	size="small"
																	hiddenLabel
																	InputProps={{
																		...params.InputProps,
																		style: {
																			borderRadius: "8px",
																		},
																		disableUnderline: true,
																	}}
																/>
															)}
														/>
														<div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
															{typeof getIn(errors, `gcpClusterViewers`) == "string"
																? getIn(errors, `gcpClusterViewers`)
																: (getIn(errors, `gcpClusterViewers`) as string[])?.filter(
																		(e) => e != null
																  )[0]}
														</div>
													</Grid>
													<Grid item xs={12} sm={6}>
														<CompulsoryFieldsLabel fieldName="Application Owners" />
														<Autocomplete
															multiple
															options={users.map((e: any) => e.email)}
															filterSelectedOptions
															ChipProps={{ size: "small" }}
															freeSolo
															defaultValue={values.applicationRequesters}
															onChange={(e, newVal, reason) => {
																// setApplicationOwners(newVal);

																setFieldValue("applicationRequesters", newVal);
															}}
															renderInput={(params) => (
																<TextField
																	{...params}
																	variant="filled"
																	fullWidth
																	placeholder="e.g. abc121@gmail.com"
																	size="small"
																	hiddenLabel
																	InputProps={{
																		...params.InputProps,
																		style: {
																			borderRadius: "8px",
																		},

																		disableUnderline: true,
																	}}
																/>
															)}
														/>
														<div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
															{typeof getIn(errors, `applicationRequesters`) == "string"
																? getIn(errors, `applicationRequesters`)
																: (getIn(errors, `applicationRequesters`) as string[])?.filter(
																		(e) => e != null
																  )[0]}
														</div>
													</Grid>
												</Grid>
											</div>

											<div
												style={{
													backgroundColor: "white",
													padding: "24px",
													borderRadius: "8px",
													boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
													marginBottom: "24px",
												}}
											>
												<Typography
    fontFamily="Gilroy-Medium"
    fontSize="14px"
    style={{ marginBottom: "16px" }} // Keep the margin on the outer Typography
>
    {/* Use a Box with flex display to align items inline */}
    <Box sx={{ display: 'flex', alignItems: 'center' }} component="span">
        {/* Text part */}
        
		<CompulsoryFieldsLabel fieldName="Cluster Tags / Labels" />

        {/* Tooltip part (comes immediately after text within the flex container) */}
        <Tooltip
            sx={{ whiteSpace: "pre-line" }}
            title={
                <>
                    <Typography fontFamily="Gilroy-Regular" fontSize="14px" fontWeight={700}>
                        {/* Placeholder Title - Replace with actual tooltip content */}
                        Ensure these labels have been attached to the cluster
                    </Typography>
                    {/* Add more detailed content for the tooltip if needed */}
                </>
            }
            slotProps={{ tooltip: { sx: { bgcolor: '#333333' } } }}
        >
            {/* Avatar is the trigger for the Tooltip */}
            <Avatar
                src="/assets/info.svg"
                alt="info"
                sx={{
                    height: 16,
                    width: 16,
                    marginLeft: "8px", // This margin creates space between text and icon
                    cursor: 'pointer', // Good UX to show the icon is interactive
                    // Optional: Adjust vertical alignment slightly if needed
                    // verticalAlign: 'middle',
                }}
            />
        </Tooltip>
    </Box>
</Typography>
												<Divider sx={{ marginBottom: "24px" }} />
												<Grid container spacing={4} style={{ marginBottom: "16px" }}>
													<Grid item xs={12} sm={6}>
														<Typography
															gutterBottom
															fontFamily="Gilroy-Regular"
															fontSize="14px"
															ml="4px"
														>
															Key
														</Typography>
													</Grid>
													<Grid item xs={12} sm={6}>
														<Typography
															gutterBottom
															fontFamily="Gilroy-Regular"
															fontSize="14px"
															ml="4px"
														>
															Value
														</Typography>
													</Grid>
												</Grid>
												<Grid container spacing={4}>
													<Grid item xs={12} sm={6}>
														<TextField
															fullWidth
															placeholder="Enter key"
															size="medium"
															hiddenLabel
															disabled={true}
															value="cluster-owner"
															variant="filled"
															sx={{ marginBottom: "0px" }}
															InputProps={{
																disableUnderline: true,
																style: {
																	borderRadius: "8px",
																},
															}}
														/>
													</Grid>
													<Grid item xs={12} sm={6}>
														<Box display="flex" alignItems="center" justifyContent="space-between">
															<TextField
																fullWidth
																placeholder="Enter value"
																size="medium"
																value={(isEdit
																	? (cluster.clusterOwner.name as string)
																	: (user.name as string)
																)
																	.toLowerCase()
																	.replace(" ", "-")}
																hiddenLabel
																sx={{ marginBottom: "0px" }}
																variant="filled"
																disabled={true}
																InputProps={{
																	disableUnderline: true,
																	style: {
																		borderRadius: "8px",
																	},
																}}
															/>
														</Box>
													</Grid>
													<Grid item xs={12} sm={6}>
														<TextField
															fullWidth
															placeholder="Enter key"
															size="medium"
															hiddenLabel
															disabled={true}
															value="department"
															variant="filled"
															sx={{ marginBottom: "0px" }}
															InputProps={{
																disableUnderline: true,
																style: {
																	borderRadius: "8px",
																},
															}}
														/>
													</Grid>
													<Grid item xs={12} sm={6}>
														<Box display="flex" alignItems="center" justifyContent="space-between">
															<TextField
																fullWidth
																placeholder="Enter value"
																size="medium"
																value={values.departmentValue}
																hiddenLabel
																variant="filled"
																name="departmentValue"
																sx={{
																	"& .MuiFilledInput-root": {
																		border:
																			errors["departmentValue"] && touched["departmentValue"]
																				? "1px solid red"
																				: "1px solid #00000000",
																		"&.Mui-focused": {
																			backgroundColor: "white",
																			border: "1px solid black",
																		},
																	},
																	marginBottom: "0px",
																}}
																onChange={handleChange}
																onBlur={handleBlur}
																InputProps={{
																	disableUnderline: true,
																	style: {
																		borderRadius: "8px",
																	},
																}}
															/>
														</Box>
														<div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
															<ErrorMessage name="departmentValue" component="div" />
														</div>
													</Grid>
													<Grid item xs={12} sm={6}>
														<TextField
															fullWidth
															placeholder="Enter key"
															size="medium"
															hiddenLabel
															disabled={true}
															value="cost-center"
															variant="filled"
															sx={{ marginBottom: "0px" }}
															InputProps={{
																disableUnderline: true,
																style: {
																	borderRadius: "8px",
																},
															}}
														/>
													</Grid>
													<Grid item xs={12} sm={6}>
														<Box display="flex" alignItems="center" justifyContent="space-between">
															<TextField
																fullWidth
																placeholder="Enter value"
																size="medium"
																value={values.costCenterValue}
																hiddenLabel
																variant="filled"
																name="costCenterValue"
																sx={{
																	"& .MuiFilledInput-root": {
																		border:
																			errors["costCenterValue"] && touched["costCenterValue"]
																				? "1px solid red"
																				: "1px solid #00000000",
																		"&.Mui-focused": {
																			backgroundColor: "white",
																			border: "1px solid black",
																		},
																	},
																	marginBottom: "0px",
																}}
																onChange={handleChange}
																onBlur={handleBlur}
																InputProps={{
																	disableUnderline: true,
																	style: {
																		borderRadius: "8px",
																	},
																}}
															/>
														</Box>
														<div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
															<ErrorMessage name="costCenterValue" component="div" />
														</div>
													</Grid>
												</Grid>

												<div>
												<FormControlLabel
    control={
        <Checkbox
			name="agreement1" // Link to Formik values
            checked={values.agreement1} // Controlled by Formik state
            onChange={handleChange} // Use Formik's handler
            sx={{
                color: 'black', // Keep checkbox black
                '&.Mui-checked': {
                    color: 'black',
                },
            }}
        /> // Uncontrolled checkbox
    }
    // --- Modified Label ---
    label={
        <> {/* Use a React Fragment to group the text and the styled asterisk */}
            I verify that Intellicore service account
			<Box component="span" sx={{ color: 'red', pl: '2px' }}>({config?.serviceAccountRunner}) </Box>{/* Existing text */}
            {/* Add a span for the asterisk and style it red */}
			have the access to existing cluster project or account.
            <Box component="span" sx={{ color: 'red', pl: '2px' }}>*</Box>
            {/* Alternatively, using plain HTML span: <span style={{ color: 'red', paddingLeft: '2px' }}>*</span> */}
        </>
    }
    // --- End Modified Label ---
    sx={{ display: 'block', marginBottom: '8px', '& .MuiTypography-root': { fontSize: '14px', fontFamily: 'Gilroy-Regular' } }} // Style label container if needed
/>

<FormControlLabel
    control={
        <Checkbox
			name="agreement2" // Link to Formik values
            checked={values.agreement2} // Controlled by Formik state
            onChange={handleChange} // Use Formik's handler
            sx={{
                color: 'black', // Keep checkbox black
                '&.Mui-checked': {
                    color: 'black',
                },
            }}
        /> // Uncontrolled checkbox
    }
    // --- Modified Label ---
    label={
        <> {/* Use a React Fragment to group the text and the styled asterisk */}
            I verify that Intellicore IDP VPC has network conectivity with existing cluster VPC network over
			<Box component="span" sx={{ color: 'red', pl: '2px' }}> PORT 443</Box>
            <Box component="span" sx={{ color: 'red', pl: '2px' }}>*</Box>
            {/* Alternatively, using plain HTML span: <span style={{ color: 'red', paddingLeft: '2px' }}>*</span> */}
        </>
    }
    // --- End Modified Label ---
    sx={{ display: 'block', marginBottom: '8px', '& .MuiTypography-root': { fontSize: '14px', fontFamily: 'Gilroy-Regular' } }} // Style label container if needed
/>

</div>


												<Grid container spacing={4} mt="8px">
													{values.costLabelPairs.map((e: any, index: number) => (
														<>
															<Grid item xs={12} sm={6}>
																<TextField
																	fullWidth
																	placeholder="Enter key"
																	size="medium"
																	hiddenLabel
																	value={e.cost}
																	sx={{
																		"& .MuiFilledInput-root": {
																			border: "1px solid #00000000",
																			"&.Mui-focused": {
																				backgroundColor: "white",
																				border: "1px solid black",
																			},
																		},
																		marginBottom: "0px",
																	}}
																	variant="filled"
																	onChange={(event) => {
																		setFieldValue("costLabelPairs", [
																			...values.costLabelPairs.map((pair: any) => {
																				if (e.id === pair.id) {
																					pair.cost = event.target.value || "";
																				}
																				return pair;
																			}),
																		]);
																	}}
																	InputProps={{
																		disableUnderline: true,
																		style: {
																			borderRadius: "8px",
																		},
																	}}
																/>
																<div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
																	{getIn(errors, `costLabelPairs[${index}].cost`)}
																</div>
															</Grid>
															<Grid item xs={12} sm={6}>
																<Box
																	display="flex"
																	alignItems="center"
																	justifyContent="space-between"
																>
																	<TextField
																		fullWidth
																		placeholder="Enter value"
																		size="medium"
																		sx={{
																			"& .MuiFilledInput-root": {
																				border: "1px solid #00000000",
																				"&.Mui-focused": {
																					backgroundColor: "white",
																					border: "1px solid black",
																				},
																			},
																			marginBottom: "0px",
																		}}
																		value={e.label}
																		hiddenLabel
																		variant="filled"
																		onChange={(event) => {
																			setFieldValue("costLabelPairs", [
																				...values.costLabelPairs.map((pair: any) => {
																					if (e.id === pair.id) {
																						pair.label = event.target.value || "";
																					}
																					return pair;
																				}),
																			]);
																		}}
																		InputProps={{
																			disableUnderline: true,
																			style: {
																				borderRadius: "8px",
																			},
																		}}
																	/>

																	<Button
																		sx={{ marginLeft: "8px" }}
																		onClick={() => {
																			let clpArr = values.costLabelPairs.filter((pair: any) => {
																				return pair.id != e.id;
																			});

																			setFieldValue("costLabelPairs", clpArr);
																		}}
																	>
																		<img alt="close-icon" src="/assets/close-icon.svg" />
																	</Button>
																</Box>
																<div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
																	{getIn(errors, `costLabelPairs[${index}].label`)}
																</div>
															</Grid>
														</>
													))}
												</Grid>

												{/* <Button
													variant="text"
													sx={{ color: "#000000", textTransform: "none", mt: "16px" }}
													onClick={() =>
														setFieldValue("costLabelPairs", [
															...values.costLabelPairs,
															{ cost: "", label: "", id: crypto.randomUUID() },
														])
													}
												>
													+ Add Key Value Pair
												</Button> */}
											</div>
										</Box>

										<Box display="flex" justifyContent="flex-end" gap={2}>
											<Button
												sx={{
													color: "black",
													borderColor: "black",
													fontWeight: "bold",
													textTransform: "none",
													px: 4,
												}}
												variant="outlined"
												size="large"
												onClick={() => navigate("/clusters")}
											>
												Cancel
											</Button>
											<Button
												sx={{
													backgroundColor: "black",
													color: "white",
													fontWeight: "bold",
													"&:hover": {
														backgroundColor: "white",
														color: "black",
														borderColor: "black",
													},
													textTransform: "none",
													px: 4,
												}}
												variant="contained"
												size="large"
												type="submit"
												disabled={!(isValid && dirty) || isViewer}
											>
												{isEdit ? "Save Changes" : "Add Existing Cluster"}
											</Button>
										</Box>
									</Form>
								);
							}}
						</Formik>
					)}
				</Grid>
				<Grid item xs={1}></Grid>
			</Grid>
		</Container>
	);
};

//export default ExistingClusterForm;
const ExistingClusterForm: React.FC<ClusterFormProps> = ({ isEdit }) => {
    const theme = useTheme();

    const getInitialDrawerState = () => {
        if (typeof window !== 'undefined') {
            const storedState = localStorage.getItem('isFaradayDrawerOpen');
            return storedState !== null ? JSON.parse(storedState) : true;
        }
        return true;
    };
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
    };

    // Using your remembered calculation for marginLeft
    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;
	const copyIsEdit = isEdit;
    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer
                // Assuming "integrations" is the correct index for Kyverno page
                // If Kyverno has its own top-level menu item, update this index.
                index={"clusters"} // Or a more specific index like "integrations-kyverno" if applicable
                onStateChange={handleDrawerStateChange}
            />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    // bgcolor: '#fafafa', // bgcolor commented out as per your previous change
                    p: 0,
                    transition: theme.transitions.create('margin', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                    // Using your remembered unit and calculation for marginLeft
                    marginLeft: `${currentMarginLeft}vh`,
                }}
            >
                <ExistingClusterFormContent isEdit={copyIsEdit} />
            </Box>
        </Box>
    );
};

export default ExistingClusterForm;

